import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";

import type { CustomerStatus } from "@attendik/types/admin-schemas";

import { api } from "../api/client.js";
import { isApiError } from "../api/errors.js";
import { PageHeader } from "../components/AppLayout.js";
import { Button } from "../components/ui/button.js";
import { Card } from "../components/ui/card.js";
import {
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader
} from "../components/ui/dialog.js";
import { Input } from "../components/ui/input.js";
import { Label } from "../components/ui/label.js";
import { Select } from "../components/ui/select.js";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "../components/ui/table.js";
import { EmptyState, ErrorState, LoadingState } from "../components/states.js";
import { formatDate, slugify } from "../lib/format.js";
import { useAsync } from "../lib/useAsync.js";
import { StatusBadge, customerStatusVariant } from "./shared/pills.js";

/** Customers list + create flow (portal_admin only — build plan P2-T6). */
export function CustomersPage(): JSX.Element {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<CustomerStatus | "">("");
  const [creating, setCreating] = useState(false);

  const { data, loading, error, reload } = useAsync(
    () => api.listCustomers(statusFilter || undefined),
    [statusFilter]
  );

  return (
    <>
      <PageHeader
        title="Customers"
        description="Independent organisations using the platform."
        actions={
          <Button onClick={() => setCreating(true)}>Create customer</Button>
        }
      />

      <div className="mb-4 flex items-center gap-3">
        <Label htmlFor="status-filter" className="text-muted-foreground">
          Status
        </Label>
        <Select
          id="status-filter"
          className="w-40"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as CustomerStatus | "")}
        >
          <option value="">All</option>
          <option value="ACTIVE">Active</option>
          <option value="DISABLED">Disabled</option>
        </Select>
      </div>

      <Card>
        {loading ? (
          <LoadingState />
        ) : error ? (
          <ErrorState message={error} onRetry={reload} />
        ) : !data || data.length === 0 ? (
          <EmptyState
            title="No customers"
            description="Create the first customer to get started."
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((customer) => (
                <TableRow
                  key={customer.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/customers/${customer.id}`)}
                >
                  <TableCell className="font-medium">{customer.name}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {customer.slug}
                  </TableCell>
                  <TableCell>
                    <StatusBadge variant={customerStatusVariant(customer.status)}>
                      {customer.status}
                    </StatusBadge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDate(customer.created_at)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {creating ? (
        <CreateCustomerDialog
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            reload();
          }}
        />
      ) : null}
    </>
  );
}

function CreateCustomerDialog({
  onClose,
  onCreated
}: {
  onClose: () => void;
  onCreated: () => void;
}): JSX.Element {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugEdited, setSlugEdited] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleNameChange = (value: string): void => {
    setName(value);
    if (!slugEdited) {
      setSlug(slugify(value));
    }
  };

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.createCustomer(name.trim(), slug.trim());
      onCreated();
    } catch (err) {
      if (isApiError(err) && err.code === "SLUG_TAKEN") {
        setError("That slug is already taken. Choose another.");
      } else if (isApiError(err) && err.status === 400) {
        setError("Slug must use lowercase letters, digits and hyphens only.");
      } else {
        setError("Could not create the customer. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onClose={onClose} labelledBy="create-customer-title">
      <DialogHeader
        id="create-customer-title"
        title="Create customer"
        description="Add a new organisation to the platform."
        onClose={onClose}
      />
      <form onSubmit={handleSubmit}>
        <DialogBody>
          <div className="space-y-1.5">
            <Label htmlFor="customer-name">Name</Label>
            <Input
              id="customer-name"
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="customer-slug">Slug</Label>
            <Input
              id="customer-slug"
              value={slug}
              onChange={(e) => {
                setSlug(e.target.value);
                setSlugEdited(true);
              }}
              placeholder="lowercase-with-hyphens"
              required
            />
            <p className="text-xs text-muted-foreground">
              Lowercase letters, digits and hyphens. Cannot be changed later.
            </p>
          </div>
          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? "Creating…" : "Create"}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
